import { gql } from '@apollo/client';

export const MEDIA_FRAGMENT = gql`
  fragment MediaFields on Media {
    id
    url
    mediaType
    mediaIds
    mimeType
    width
    height
    blurhash
  }
`;

// `urlPath`: the category's hub (web plan W3.1; a level-3 category's is its
// level-2 parent's), which the category chips on entity pages link to.
export const CATEGORY_FRAGMENT = gql`
  fragment CategoryFields on Category {
    id
    title
    urlPath
  }
`;

export const TAG_FRAGMENT = gql`
  fragment TagFields on Tag {
    id
    title
  }
`;

// Backend B3.4 staff content. `seoOverrides` (title / description only on the
// public API) wins over the head formula in @/seo/templates; robots never come
// from it. `openingHours` is `[]` when nothing was entered.
export const SEO_OVERRIDES_FRAGMENT = gql`
  fragment SeoOverridesFields on SeoOverrides {
    title
    description
  }
`;

export const OPENING_HOURS_FRAGMENT = gql`
  fragment OpeningHoursFields on OpeningHours {
    days
    opens
    closes
  }
`;

// Backend B3.5 structured offer: the clean `headline` (staff-typed, else the
// cleaned name) that titles and H1s use, and the discount titles and
// descriptions read — never parsed from the name or the caption. Selected on
// every promotion row a head reads (a brand's "Up to {X}% Off" comes from its
// listed promotions).
export const PROMOTION_OFFER_FRAGMENT = gql`
  fragment PromotionOfferFields on Promotion {
    headline
    discountType
    discountMin
    discountMax
  }
`;

// Every card and minimal fragment selects `urlPath`, the backend's canonical
// path (backend B1.4, master D-2): cards, rows and pins link through
// `entityPath` (@/seo/urls), which uses it and falls back to the id path only
// for data fetched without it (web plan W2.2). No link is built by hand.
export const BRAND_MINIMAL_FRAGMENT = gql`
  fragment BrandMinimalFields on Brand {
    id
    name
    seoName
    urlPath
    logo {
      ...MediaFields
    }
    isFavorite
    promotions {
      id
    }
  }
  ${MEDIA_FRAGMENT}
`;

export const STORE_MINIMAL_FRAGMENT = gql`
  fragment StoreMinimalFields on Store {
    id
    name
    urlPath
    logo {
      ...MediaFields
    }
    location
    isFavorite
    promotions {
      id
    }
  }
  ${MEDIA_FRAGMENT}
`;

export const SHOP_MINIMAL_FRAGMENT = gql`
  fragment ShopMinimalFields on Shop {
    id
    name
    urlPath
    logo {
      ...MediaFields
    }
    location
    isFavorite
    promotions {
      id
    }
  }
  ${MEDIA_FRAGMENT}
`;

export const PROMOTION_MINIMAL_FRAGMENT = gql`
  fragment PromotionMinimalFields on Promotion {
    id
    name
    urlPath
    ...PromotionOfferFields
    description
    startDate
    endDate
    isUpcoming
    status
    verified
    media {
      ...MediaFields
    }
    brand {
      ...BrandMinimalFields
    }
    stores {
      ...StoreMinimalFields
    }
    shops {
      ...ShopMinimalFields
    }
  }
  ${PROMOTION_OFFER_FRAGMENT}
  ${MEDIA_FRAGMENT}
  ${BRAND_MINIMAL_FRAGMENT}
  ${STORE_MINIMAL_FRAGMENT}
  ${SHOP_MINIMAL_FRAGMENT}
`;

// Lean fragment for promotion LIST cards. PromotionCard only renders the
// promotion's own fields + media, the brand's name/logo, and a COUNT of
// stores/shops (`stores.length + shops.length`). It does NOT need each
// nested store/shop's logo/location/promotions/isFavorite, nor the brand's
// promotions/isFavorite — so this drops the deep per-row fan-out that
// PromotionMinimalFields pulls. Detail pages keep the full fragment.
// `headline` (backend B3.5): the card's title and the list's ItemList names,
// the same text as the promotion page's H1.
export const PROMOTION_CARD_FRAGMENT = gql`
  fragment PromotionCardFields on Promotion {
    id
    name
    urlPath
    headline
    description
    startDate
    endDate
    isUpcoming
    status
    verified
    media {
      ...MediaFields
    }
    brand {
      id
      name
      seoName
      urlPath
      logo {
        ...MediaFields
      }
    }
    stores {
      id
    }
    shops {
      id
    }
  }
  ${MEDIA_FRAGMENT}
`;

export const SOCIALS_FRAGMENT = gql`
  fragment SocialsFields on Socials {
    facebook
    tiktok
    twitter
    instagram
    youtube
    linkedIn
  }
`;

// The detail fragments carry the URL contract (backend B1.4, web plan W0.16):
// `urlPath`, the canonical path every link, canonical and share uses (the
// page moves to it when opened elsewhere), and `seo.robots`, the exact robots
// the page emits (`buildHead` in @/seo/templates prefers it over the
// client-side rules). The promotion's `phase` too (`promotionPhase` in
// @/seo/lifecycle prefers it over the dates).
export const BRAND_FULL_FRAGMENT = gql`
  fragment BrandFullFields on Brand {
    id
    name
    seoName
    urlPath
    seo {
      robots
      alternates {
        hreflang
        url
      }
    }
    description
    seoOverrides {
      ...SeoOverridesFields
    }
    website
    phoneNumber
    mobileNumber
    socials {
      ...SocialsFields
    }
    viewCounts
    clickCounts
    subscriberCounts
    isFavorite
    isSubscribed
    logo {
      ...MediaFields
    }
    coverImage {
      ...MediaFields
    }
    categories {
      ...CategoryFields
    }
    categoriesPaths
    tags {
      ...TagFields
    }
    cityCounts {
      city {
        name
        slug
      }
      stores
      brandCityPath
    }
    # Lean on purpose (audit F1): a chain has hundreds of these and the page
    # carries every one of them in its hydration data. A row needs a name, a
    # link, a small logo, the address and the map link; not each logo's
    # blurhash / media ids / mime type, nor the store's promotion ids, nor a cover.
    brandStores {
      id
      name
      urlPath
      logo {
        id
        url
        width
        height
      }
      location
      isFavorite
      address
      city
      googleMapsPlaceID
    }
    promotions {
      ...PromotionMinimalFields
    }
    ogImageUrl
  }
  ${MEDIA_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${TAG_FRAGMENT}
  ${STORE_MINIMAL_FRAGMENT}
  ${PROMOTION_MINIMAL_FRAGMENT}
  ${SOCIALS_FRAGMENT}
  ${SEO_OVERRIDES_FRAGMENT}
`;

// The store page (web plan W0.10 / W3.8) reads `activePromotions` directly
// from the store (backend B3.7a: running + upcoming, store's own first) and
// its brand's other stores, lean ("Other {Brand} stores in {City}"; the rows
// fall back to the brand's logo, so no per-store media is fetched).
// `area` (B3.2) drives the H1 formula "{Brand} – {Area}, {City}" (W3.8).
export const STORE_FULL_FRAGMENT = gql`
  fragment StoreFullFields on Store {
    id
    name
    urlPath
    seo {
      robots
      alternates {
        hreflang
        url
      }
    }
    description
    openingHours {
      ...OpeningHoursFields
    }
    seoOverrides {
      ...SeoOverridesFields
    }
    location
    googleMapsPlaceID
    address
    area
    city
    website
    phoneNumber
    mobileNumber
    socials {
      ...SocialsFields
    }
    viewCounts
    clickCounts
    subscriberCounts
    isFavorite
    isSubscribed
    logo {
      ...MediaFields
    }
    coverImage {
      ...MediaFields
    }
    categories {
      ...CategoryFields
    }
    categoriesPaths
    tags {
      ...TagFields
    }
    brand {
      id
      name
      seoName
      urlPath
      logo {
        ...MediaFields
      }
      isFavorite
      brandStores {
        id
        name
        urlPath
        city
        address
        location
        googleMapsPlaceID
        isFavorite
      }
    }
    activePromotions {
      ...PromotionMinimalFields
    }
  }
  ${MEDIA_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${TAG_FRAGMENT}
  ${PROMOTION_MINIMAL_FRAGMENT}
  ${SOCIALS_FRAGMENT}
  ${OPENING_HOURS_FRAGMENT}
  ${SEO_OVERRIDES_FRAGMENT}
`;

export const SHOP_FULL_FRAGMENT = gql`
  fragment ShopFullFields on Shop {
    id
    name
    urlPath
    seo {
      robots
      alternates {
        hreflang
        url
      }
    }
    description
    openingHours {
      ...OpeningHoursFields
    }
    seoOverrides {
      ...SeoOverridesFields
    }
    location
    googleMapsPlaceID
    address
    city
    website
    phoneNumber
    mobileNumber
    socials {
      ...SocialsFields
    }
    viewCounts
    clickCounts
    subscriberCounts
    isFavorite
    isSubscribed
    logo {
      ...MediaFields
    }
    coverImage {
      ...MediaFields
    }
    categories {
      ...CategoryFields
    }
    categoriesPaths
    tags {
      ...TagFields
    }
    promotions {
      ...PromotionMinimalFields
    }
  }
  ${MEDIA_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${TAG_FRAGMENT}
  ${PROMOTION_MINIMAL_FRAGMENT}
  ${SOCIALS_FRAGMENT}
  ${OPENING_HOURS_FRAGMENT}
  ${SEO_OVERRIDES_FRAGMENT}
`;

// The promotion page (web plan W0.9) also reads `brand.brandStores { id city }`
// ("Where it's valid" for a brand-wide promotion that names no store: the
// brand's cities) and each category's `path` + parent (the L2 category of the
// "Similar {Category} deals" rail; the chips' search path), and
// `brand.isSubscribed` for its "Follow {Brand} for alerts" on upcoming and
// ended promotions (master D-4; false for a guest). The backend B3.5 offer:
// headline and discount (the head), where it can be used, terms, the brand's
// own offer page and `lastVerifiedAt` ("Last verified {date}" without an end
// date); B3.4's `seoOverrides`.
export const PROMOTION_FULL_FRAGMENT = gql`
  fragment PromotionFullFields on Promotion {
    id
    name
    urlPath
    seo {
      robots
      alternates {
        hreflang
        url
      }
    }
    seoOverrides {
      ...SeoOverridesFields
    }
    phase
    ...PromotionOfferFields
    availabilityScope
    terms
    offerUrl
    lastVerifiedAt
    description
    startDate
    endDate
    isUpcoming
    status
    verified
    viewCounts
    clickCounts
    media {
      ...MediaFields
    }
    brand {
      ...BrandMinimalFields
      isSubscribed
      brandStores {
        id
        city
      }
    }
    stores {
      ...StoreMinimalFields
    }
    shops {
      ...ShopMinimalFields
    }
    tags {
      ...TagFields
    }
    categories {
      ...CategoryFields
      path
      parentCategory {
        id
        title
        path
      }
    }
    categoriesPaths
    ogImageUrl
  }
  ${MEDIA_FRAGMENT}
  ${TAG_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${BRAND_MINIMAL_FRAGMENT}
  ${STORE_MINIMAL_FRAGMENT}
  ${SHOP_MINIMAL_FRAGMENT}
  ${SEO_OVERRIDES_FRAGMENT}
  ${PROMOTION_OFFER_FRAGMENT}
`;
